<script setup lang="ts">
import {useRoute} from "vue-router";
import {computed, ref, watch} from "vue";
import {massCertificationApi} from "@/container";
import {MassCertificationNotificationBatch} from "@/core/interfaces/MassCertification";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import {ConversationMessage} from "@/conversation/interfaces";
import CommunicationBatchHeader from "@/communication/components/CommunicationBatchHeader.vue";
import HeaderBackButton from "@/core/components/header-back-button/HeaderBackButton.vue";
import ConversationMessagesTable from "@/conversation/components/ConversationMessagesTable.vue";

const route = useRoute();

const batch = ref<MassCertificationNotificationBatch>();
const messages = ref<ConversationMessage[]>();
const loading = ref(false);
const messagesLoading = ref(false);
const total = ref(0);
const page = ref(1);
const limit = ref(20);

const batchId = computed(() => {
    return Number(route.params.batchId);
});

const fetchMessages = () => {
    if (!batchId.value) {
        return;
    }

    messagesLoading.value = true;

    massCertificationApi().readBatchMessages(batchId.value, page.value, limit.value)
        .then((response) => {
            messages.value = response.data;
            total.value = response.meta?.total ?? 0;
        })
        .finally(() => {
            messagesLoading.value = false;
        });
};

const handlePageChange = (newPage: number) => {
    if (newPage === page.value) {
        return;
    }

    page.value = newPage;
    fetchMessages();
};

const handleLimitChange = (newLimit: number) => {
    if (newLimit === limit.value) {
        return;
    }

    page.value = 1;
    limit.value = newLimit;
    fetchMessages();
};

watch(batchId, (id) => {
    if (!id) {
        return;
    }

    loading.value = true;
    page.value = 1;

    massCertificationApi().readBatch(id)
        .then((data) => {
            batch.value = data;
            fetchMessages();
        })
        .finally(() => {
            loading.value = false;
        });
}, {immediate: true});
</script>

<template>
  <BaseContainer class="p-4">
    <BaseFlexSpinner v-if="loading" />
    <div v-else-if="batch" class="flex flex-col gap-4">
      <HeaderBackButton class="bg-transparent" title="Suivi des envois" />

      <BaseCard>
        <BaseCardBody class="flex flex-col gap-6">
          <CommunicationBatchHeader :batch="batch" />
        </BaseCardBody>
      </BaseCard>

      <BaseCard>
        <BaseCardBody>
          <h1>Détail par patient</h1>
          <div v-if="messages">
            <ConversationMessagesTable
              :limits="limit"
              :messages="messages"
              :total="total"
              :loading="messagesLoading"
              :extends="['flow', 'sent_at']"
              @update:page="handlePageChange"
              @update:limits="handleLimitChange"
            />
          </div>
        </BaseCardBody>
      </BaseCard>
    </div>
  </BaseContainer>
</template>

<style scoped>

</style>
